/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { ApiErrorDecoder } from './api-error-decoder';
import { NavigatorService } from '@services/navigator/navigator.service';
import { AppService } from '@services/app/app.service';
import { UiService } from '@services/ui/ui.service';
import { LabelsService } from '@services/labels/labels.service';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiErrorDTO } from '@api/models';
import { ERRORS } from '@core/models';
import { ToastController } from '@ionic/angular';
import { LoggerService } from '@core/services/logger/logger.service';
import { SecurityService } from '@core/services/security/security.service';

export interface ReqErrorConfigModel {
  showError: boolean;
  defaultMessage: string;
}

@Injectable({
  providedIn: 'root'
})
export class HttpErrorInterceptorService implements HttpInterceptor {
  constructor(
    private appService: AppService,
    private uiService: UiService,
    private navigator: NavigatorService,
    private labelsService: LabelsService,
    private toastController: ToastController,
    private loggerService: LoggerService,
    private securityService: SecurityService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const errorConfig = this.createErrorConfig();

    this.logRequest(req);

    this.uiService.restoreNextError();

    return next
      .handle(req)
      .pipe(
        catchError((event: HttpErrorResponse) =>
          this.handleError(errorConfig, event)
        )
      );
  }

  /**
   * crea la configurazione per la gestione dell'errore della richiesta corrente
   */
  private createErrorConfig(): ReqErrorConfigModel {
    const showError = this.uiService.isNextErrorToShow();
    const defaultMessage =
      this.uiService.getNextErrorMessage() || 'err.something_went_wrong';

    return {
      showError,
      defaultMessage
    };
  }

  /**
   * gestisce l'errore
   */
  private handleError(
    config: ReqErrorConfigModel,
    event: HttpErrorResponse
  ): Observable<never> {
    this.logError(event);

    this.redirectWhenUnauthorized(event);

    if (config && config.showError && !this.isLogUrl(event.url)) {
      this.showErrorToast(event, config.defaultMessage);
    }

    return throwError(event);
  }

  /**
   * effettua redirect verso pagina non autorizzato se viene rilevato errore 401
   */
  private redirectWhenUnauthorized(event: HttpErrorResponse): void {
    if (
      event.status !== 401 ||
      this.appService.getConfig().generic.redirectUnauthorized !== true
    ) {
      return;
    }

    this.navigator.navigateToErrorPage(ERRORS.sessionExpired);
  }

  /**
   * mostra messaggio di errore
   */
  private async showErrorToast(
    event: HttpErrorResponse,
    defaultMessage: string
  ): Promise<void> {
    const error = event.error as ApiErrorDTO;
    const decoder = new ApiErrorDecoder(error, defaultMessage);

    if (this.appService.getConfig().generic.errorDisplay === 'complete') {
      this.showCompleteErrorToast(decoder);
    } else {
      this.showBaseErrorToast(decoder);
    }
  }

  /**
   * visualizza toast message di base
   */
  private async showBaseErrorToast(decoder: ApiErrorDecoder): Promise<void> {
    const message = decoder.getErrorMessage();

    this.uiService.showErrorToast(message);
  }

  /**
   * visualizza toast message avanzato con codice errore
   */
  private async showCompleteErrorToast(
    decoder: ApiErrorDecoder
  ): Promise<void> {
    const uuid = decoder.getErrorPartialUuid();
    const message = decoder.getErrorMessage();

    const toast = await this.toastController.create({
      header: this.labelsService.getLabel(message),
      message: `Error code: ${uuid}`,
      position: 'top',
      color: 'danger',
      duration: 8000,
      cssClass: 'complete-error-toast',
      buttons: [
        {
          icon: 'close',
          role: 'cancel',
          handler: () => toast.dismiss()
        }
      ]
    });

    await toast.present();
  }

  /**
   * logga la richiesta solo se non è l'api di log
   */
  private logRequest(req: HttpRequest<any>): void {
    if (this.isUrlToLog(req.url)) {
      this.loggerService.logHttpReq(req);
    }
  }

  /**
   * logga l'errore solo se non è l'api di log
   */
  private logError(event: HttpErrorResponse): void {
    if (this.isUrlToLog(event.url)) {
      this.loggerService.logError(event);
    }
  }

  /**
   * stabilisce se l'url è da loggaree
   */
  private isUrlToLog(url: string): boolean {
    return !this.isLogUrl(url) && !this.securityService.isUrlOutOfAuth(url);
  }

  /**
   * stabilisce se si tratta dell'api di log
   */
  private isLogUrl(url: string): boolean {
    return url && url.includes('user/action');
  }
}
